import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

/**
 * EventLocationsManager — admin control to CRUD the per-event location list.
 * Appears only when editing an existing event (needs an event_id).
 * Uses the dedicated /api/events/{id}/locations endpoints so changes are
 * saved immediately without touching the main form payload.
 */
export default function EventLocationsManager({ eventId }) {
  const [locations, setLocations] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#8b5cf6');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/events/${eventId}/locations`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setLocations(d.locations || []);
        }
      } catch { /* ignore */ }
    })();
  }, [eventId]);

  const addLocation = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/events/${eventId}/locations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color: newColor }),
      });
      if (!r.ok) throw new Error('failed');
      const loc = await r.json();
      setLocations([...locations, loc]);
      setNewName('');
    } catch { toast.error('Could not add location'); }
    finally { setSaving(false); }
  };

  const deleteLocation = async (locId) => {
    setLocations(prev => prev.filter(l => l.id !== locId));
    try {
      await fetch(`${BACKEND_URL}/api/events/${eventId}/locations/${locId}`, {
        method: 'DELETE', credentials: 'include',
      });
    } catch { toast.error('Could not delete'); }
  };

  if (!eventId) {
    return (
      <p className="text-[11px] text-slate-400 italic">
        Save the event first, then add locations here.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="event-locations-manager">
      {locations.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {locations.map(l => (
            <span
              key={l.id}
              data-testid={`location-chip-${l.id}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border"
              style={{ backgroundColor: `${l.color}15`, borderColor: `${l.color}50`, color: l.color }}>
              📍 {l.name}
              <button
                type="button"
                onClick={() => deleteLocation(l.id)}
                aria-label={`Delete ${l.name}`}
                data-testid={`delete-location-${l.id}`}
                className="hover:bg-slate-200 rounded-full p-0.5">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 italic">No locations added yet.</p>
      )}
      <div className="flex gap-2 items-end">
        <div className="flex-1 min-w-0">
          <Label className="text-[11px]">New location name</Label>
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLocation(); } }}
            placeholder="e.g. Auditorium"
            data-testid="new-location-input" />
        </div>
        <div>
          <Label className="text-[11px]">Colour</Label>
          <input
            type="color"
            value={newColor}
            onChange={e => setNewColor(e.target.value)}
            data-testid="new-location-color"
            className="block w-10 h-9 rounded border border-slate-200 cursor-pointer" />
        </div>
        <Button
          type="button"
          size="sm"
          onClick={addLocation}
          disabled={saving || !newName.trim()}
          data-testid="add-location-btn">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}
